// The analysis cache's contract (P3.8) — FR-12, Architecture §5.
//
// FR-12: "Analysis results are cached locally (keyed by content hash); reloading a known track is
// effectively instant and never re-analyzes." Architecture §5 puts the records in IndexedDB and the
// waveform blobs in OPFS.
//
// **This file is the half that can be tested.** IndexedDB and OPFS exist only in a browser, so the
// cache itself (`/library/analysis-cache.ts`) is a shell that cannot run under Node — the same split
// `analysis-host.ts` has from `platform/types/analysis.ts`, and for the same reason: everything that
// could be *wrong* lives on the testable side. What is here is the record shape, the schema version
// that invalidates it, and the two predicates that decide whether a stored record may be used.
//
// FR-44 applies and is worth stating plainly even though nothing here sends anything anywhere: this
// is local storage. The content hash is derived from bytes the user already has, the record holds no
// filename, and none of it is telemetry's business.

import type { AnalysisStages } from './analysis';
import type { GridOverride } from './grid-edit';
import type { WaveformMipmapLayout } from './waveform-mipmap';

/**
 * Bumped whenever a stored record could be **wrong** rather than merely old.
 *
 * ⚠️ **This is the version of the analysis, not of the database.** P3.8's rule states the reason
 * exactly: "P3.3's detector will improve and stale grids are worse than no grids — a beatgrid from a
 * fixed bug is a lie the user has no way to see." A cached BPM carries no evidence of which detector
 * produced it, so the only defence is to throw the whole generation away when the detector changes.
 *
 * **Bump this when any of these change**, because each alters what a stage would produce for the
 * same bytes:
 *
 *   * a detector's arithmetic — `tempo.ts`'s octave rules, `grid.ts`'s fit, `key.ts`'s templates,
 *     `loudness.ts`'s filters;
 *   * the mipmap ladder or bucket record (`WAVEFORM_MIPMAP_LADDER`), which changes the blob's shape;
 *   * any `StageValues` field, added or reinterpreted.
 *
 * **Do not bump it** for a UI change, a rename, or a comment. A needless bump costs every user their
 * whole library's analysis and buys nothing.
 *
 * P3.4's two pinned defects are the concrete case waiting to happen: when the sparse-backbeat miss or
 * the confidence inversion is fixed, every record analysed before the fix is wrong in a way no user
 * can see, and this is the line that clears them.
 *
 * ---
 *
 * ⚠️ **Bumped to 2 at P3.12, and not for a detector change — to fix a bug P3.10 shipped.**
 *
 * This number is also the **IndexedDB version** (`DATABASE_VERSION` in `analysis-cache.ts`), which
 * means `onupgradeneeded` — the only place object stores can be created — fires **only when it
 * changes**. P3.10 added the `cues` store and left this at 1. For any user whose database already
 * existed from P3.8 or P3.9, the upgrade therefore never ran, the `cues` store was never created,
 * and every cue write since has failed silently into the `catch` that turns a cache failure into a
 * no-op. Hot cues have not persisted for those users at all.
 *
 * It went unnoticed because a **fresh** database gets the store: on a machine with no prior data the
 * upgrade runs once and creates whatever the current code asks for. P3.10's "cues survived a full
 * page reload and a rename" was true, and true only there.
 *
 * P3.12 adds a third store, hits the identical wall, and bumping clears both — the upgrade now runs
 * for everyone and creates `cues` and `grid-overrides` together. The cost is one re-analysis per
 * track, which is a cache doing its job.
 *
 * **So the rule above needs one more clause: bump this when the set of object stores changes, too.**
 * That is a real conflation — "the detector changed" and "the database grew a table" are different
 * events sharing one number, and the second now forces the first's cost. Parked in
 * `BACKLOG-NOTES.md` rather than refactored inside P3.12; `storeNamesAt` below is the guard that
 * stops it being forgotten a third time.
 */
export const ANALYSIS_SCHEMA_VERSION = 2;

/**
 * Every object store the cache expects to exist, at the current version.
 *
 * **A guard against the exact bug documented above.** `analysis-cache.ts` pins its store names
 * against this list, so adding a store without bumping `ANALYSIS_SCHEMA_VERSION` fails a test in
 * Node instead of failing silently in a browser months later. It cannot check what a *user's*
 * database contains — nothing in Node can — but it does catch the mistake at the point it is made,
 * which is the only place it is cheap.
 */
export const CACHE_STORE_NAMES = ['records', 'cues', 'grid-overrides'] as const;

/**
 * A track's identity: the SHA-256 of its bytes, lowercase hex.
 *
 * A branded string rather than a bare one, so a filename or a title cannot be passed where a key
 * belongs. The whole point of FR-12's "content hash" is that identity comes from the bytes, and the
 * type is the cheapest place to hold that line.
 */
export type ContentHash = string & { readonly __brand: 'ContentHash' };

const HEX_64 = /^[0-9a-f]{64}$/;

export function isContentHash(value: string): value is ContentHash {
  return HEX_64.test(value);
}

/** Narrow a string that is known to be a digest. Throws rather than corrupting the keyspace. */
export function asContentHash(value: string): ContentHash {
  if (!isContentHash(value)) {
    throw new TypeError(
      `Not a content hash: expected 64 lowercase hex characters, got ${JSON.stringify(value.slice(0, 24))}…`,
    );
  }
  return value;
}

/**
 * What is stored in IndexedDB for one track.
 *
 * **No filename, and that is FR-12's rule rather than FR-44's caution.** "The same track renamed is
 * the same track": a record that remembered what the file was called would invite a lookup by name,
 * and the first time those two disagreed the cache would be wrong. The title the UI shows comes from
 * the `File` the user just dropped, every time, and is never read back from here.
 *
 * The waveform is **not** in this record — Architecture §5 puts blobs in OPFS, and a multi-megabyte
 * `ArrayBuffer` inside an IndexedDB row would be read in full every time anything wanted a BPM.
 * `mipmapBytes` is how a reader knows what to expect from OPFS before opening it.
 */
export interface CachedAnalysis {
  readonly hash: ContentHash;
  readonly schemaVersion: number;
  /**
   * Bytes of the source file.
   *
   * **A second key, checked on every hit.** SHA-256 makes a collision a non-event, so this is not
   * really about collisions: it is about a truncated download, a half-written file, or an OPFS blob
   * that belongs to a different generation — cases where the hash is right because the bytes are
   * right, and something else is not. It costs one integer comparison.
   */
  readonly fileBytes: number;
  /** Decoded frame count and rate. Both are properties of *this machine's* decode, not of the file
   * — `decodeAudioData` resamples to the context rate — so a record made at 44.1 kHz must not be
   * used to draw a waveform decoded at 48. `isUsable` enforces it. */
  readonly frameCount: number;
  readonly sampleRateHz: number;
  /** The layout the blob was written with. Recomputed on read and compared, never trusted. */
  readonly layout: WaveformMipmapLayout;
  /** Length of the OPFS blob, so a truncated one is a miss rather than a torn waveform. */
  readonly mipmapBytes: number;
  /** Every Architecture §5 stage as it settled. Stored whole, including `skipped` and `failed`. */
  readonly stages: AnalysisStages;
  /** What the pipeline cost when it did run. Kept so a cache hit can say what it saved. */
  readonly analysisMillis: number;
  /** Unix milliseconds. For eviction, and for a human reading the store. */
  readonly storedAt: number;
}

/**
 * Is this thing, read back out of IndexedDB, shaped like a record?
 *
 * **A shape check, not a validation** — the two are different jobs and both are needed. This asks
 * whether the row is a record at all; `usabilityOf` asks whether *this* record may be used for *this*
 * track. Splitting them keeps each readable, and only this one has to care that IndexedDB hands back
 * `any`.
 *
 * The scalars are checked and `stages`/`layout` are only checked to be objects, which is a
 * deliberate line rather than laziness: those two were written by a build with the same
 * `schemaVersion`, and `usabilityOf` refuses any other, so re-validating every nested field would be
 * checking this version's output against this version's expectations. What this defends against is
 * the row being something else entirely — a hand-edited entry in devtools, a half-written value, a
 * key collision with another origin's leftovers.
 */
export function isCachedAnalysis(value: unknown): value is CachedAnalysis {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row['hash'] === 'string' &&
    isContentHash(row['hash']) &&
    typeof row['schemaVersion'] === 'number' &&
    typeof row['fileBytes'] === 'number' &&
    typeof row['frameCount'] === 'number' &&
    typeof row['sampleRateHz'] === 'number' &&
    typeof row['mipmapBytes'] === 'number' &&
    typeof row['analysisMillis'] === 'number' &&
    typeof row['storedAt'] === 'number' &&
    typeof row['layout'] === 'object' &&
    row['layout'] !== null &&
    typeof row['stages'] === 'object' &&
    row['stages'] !== null
  );
}

/**
 * A track's hot cues, stored separately from its analysis (P3.10) — FR-03's "persisted".
 *
 * ⚠️ **Separate because the two are invalidated by different things, and getting that wrong loses
 * user data.** `ANALYSIS_SCHEMA_VERSION` exists to throw away a whole generation of records when a
 * detector improves — which is right for a BPM the app computed and catastrophic for a cue point a
 * human placed by ear. P3.8 flagged this the moment the cache landed; P3.10 is where it had to be
 * resolved.
 *
 * So cues live in their own object store, which the schema upgrade does not touch. The two are still
 * keyed by the same content hash, so a renamed file finds both.
 */
export interface CachedHotCues {
  readonly hash: ContentHash;
  /**
   * Positions in **frames at the sample rate they were set at**, with `NaN` for an empty slot.
   *
   * Frames rather than seconds because that is what the transport uses, and `sampleRateHz` travels
   * with them for the same reason `CachedAnalysis` carries it: `decodeAudioData` resamples to the
   * device rate, so a cue set at 44.1 kHz names a different instant when replayed at 48. `cuesFor`
   * converts rather than refusing — unlike a mipmap, a cue point rescales exactly.
   */
  readonly frames: readonly number[];
  readonly sampleRateHz: number;
  readonly updatedAt: number;
}

/**
 * Stored cues, converted to this decode's sample rate.
 *
 * **Converted rather than refused**, which is the opposite of what `usabilityOf` does with a
 * mipmap — and the difference is real. A mipmap is an index whose buckets mean nothing at another
 * rate; a cue point is a *time*, and a time converts exactly. A DJ who plugs in different headphones
 * must not lose their cues.
 */
export function cuesFor(
  stored: CachedHotCues | null,
  sampleRateHz: number,
  slots: number,
): number[] {
  const out = new Array<number>(slots).fill(Number.NaN);
  if (stored === null || !(stored.sampleRateHz > 0) || !(sampleRateHz > 0)) {
    return out;
  }
  const scale = sampleRateHz / stored.sampleRateHz;
  for (let slot = 0; slot < slots; slot += 1) {
    const frame = stored.frames[slot];
    out[slot] =
      frame === undefined || !Number.isFinite(frame) ? Number.NaN : frame * scale;
  }
  return out;
}

/** Is this thing, read back out of IndexedDB, a cue record? */
export function isCachedHotCues(value: unknown): value is CachedHotCues {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row['hash'] === 'string' &&
    isContentHash(row['hash']) &&
    Array.isArray(row['frames']) &&
    typeof row['sampleRateHz'] === 'number' &&
    typeof row['updatedAt'] === 'number'
  );
}

/**
 * A track's manual grid correction (P3.12) — FR-13's "writing back to the analysis record".
 *
 * ⚠️ **A third store, for the reason `CachedHotCues` is a second one — only more so.** A cue is user
 * data the schema bump must not throw away. A grid override is user data that is *specifically a
 * statement that the detector was wrong*, so discarding it because the detector improved is not just
 * lossy, it is backwards. It lives beside the cues, in a store the upgrade never touches.
 *
 * **No sample rate**, unlike the cue row: both fields are already seconds (`grid-edit.ts` explains
 * the choice), so a device-rate change needs no conversion and there is no `cuesFor` equivalent to
 * get wrong.
 */
export interface CachedGridOverride extends GridOverride {
  readonly hash: ContentHash;
}

/** Is this thing, read back out of IndexedDB, a grid override? */
export function isCachedGridOverride(value: unknown): value is CachedGridOverride {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row['hash'] === 'string' &&
    isContentHash(row['hash']) &&
    typeof row['anchorSeconds'] === 'number' &&
    typeof row['beatSeconds'] === 'number' &&
    typeof row['source'] === 'string' &&
    typeof row['updatedAt'] === 'number'
  );
}

/** What a load has to know about the track before a cached record can be trusted for it. */
export interface DecodedTrackIdentity {
  readonly hash: ContentHash;
  readonly fileBytes: number;
  readonly frameCount: number;
  readonly sampleRateHz: number;
}

/**
 * May this record be used for this track?
 *
 * **Every clause is a way the cache could be silently wrong**, which is the only kind of wrong worth
 * defending against here — a cache that visibly fails just re-analyses.
 *
 *   * `schemaVersion` — the detector has changed, so the numbers are from a different program.
 *   * `hash` — the caller looked up the wrong row. Cheap, and catches a keyspace bug immediately.
 *   * `fileBytes` — the bytes are not the bytes this record was made from.
 *   * `frameCount` / `sampleRateHz` — **the subtle one.** `decodeAudioData` resamples to the
 *     `AudioContext`'s rate, which is the *device's* rate and changes when the user switches
 *     headphones. The same file decodes to a different frame count at 44.1 kHz and 48 kHz, so a
 *     mipmap built at one rate indexes wrongly at the other: the waveform would be drawn stretched
 *     and every beat marker would sit in the wrong place. Measured on this project's own hardware —
 *     the stakeholder's device reported 44.1 kHz one day and 48 kHz the next, across a browser
 *     restart, which is exactly how this would have shipped unnoticed.
 *
 * Returns a reason rather than a boolean, so a miss can be logged as *why* rather than as "no".
 */
export type CacheMiss =
  | 'no-record'
  | 'schema-version'
  | 'hash-mismatch'
  | 'file-bytes'
  | 'decode-shape'
  | 'blob-missing'
  | 'blob-size';

export function usabilityOf(
  record: CachedAnalysis | null,
  track: DecodedTrackIdentity,
): CacheMiss | null {
  if (record === null) {
    return 'no-record';
  }
  if (record.schemaVersion !== ANALYSIS_SCHEMA_VERSION) {
    return 'schema-version';
  }
  if (record.hash !== track.hash) {
    return 'hash-mismatch';
  }
  if (record.fileBytes !== track.fileBytes) {
    return 'file-bytes';
  }
  if (
    record.frameCount !== track.frameCount ||
    record.sampleRateHz !== track.sampleRateHz
  ) {
    return 'decode-shape';
  }
  return null;
}

/**
 * Is this blob the one the record describes?
 *
 * Separate from `usabilityOf` because it is answered later and by a different subsystem: the record
 * comes from IndexedDB and the blob from OPFS, and the two stores can disagree. A quota eviction can
 * take the blob and leave the row; a crash between two writes can do the same. Neither is exotic and
 * both must read as a miss.
 */
export function blobUsability(
  record: CachedAnalysis,
  blobBytes: number | null,
): CacheMiss | null {
  if (blobBytes === null) {
    return 'blob-missing';
  }
  return blobBytes === record.mipmapBytes ? null : 'blob-size';
}

/** The words a miss is logged with. A reason a human can act on, per FR-42's spirit. */
export const CACHE_MISS_REASON: Readonly<Record<CacheMiss, string>> = {
  'no-record': 'not analysed before',
  'schema-version': 'analysed by an older version of the detector',
  'hash-mismatch': 'stored under a different track',
  'file-bytes': 'the file has changed since it was analysed',
  'decode-shape': 'decoded at a different sample rate than when it was analysed',
  'blob-missing': 'the stored waveform is gone',
  'blob-size': 'the stored waveform is the wrong size',
};
